import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getSalaries, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaEye, FaPrint, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import SortOrderSelect from '../../../../shared/components/SortOrderSelect';
import { chunkArray, convertArabicNumbersToEnglish, toPositiveInteger } from '../../../../shared/utils.js/utils';

const Salaries = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const { salaries, salariesPagination, isLoading, error, success } = useSelector((state) => state.auth);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const searchTimeoutRef = useRef(null);
  const [salaryForPrint, setSalaryForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [rawSalaries, setRawSalaries] = useState([]);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [perPage, setPerPage] = useState(10); // القيمة المطبقة فعلياً
  const [perPageInput, setPerPageInput] = useState(10); // القيمة الظاهرة في الـ UI
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [isSinglePreviewOpen, setIsSinglePreviewOpen] = useState(false);
  const [singleSalaryForPreview, setSingleSalaryForPreview] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('name');
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle('sidenav.hr.salaries');
  }, [setTitle]);

  const handleApplyPerPage = () => {
    // عند الضغط على تطبيق: نطبّق القيمة الجديدة ونرجع لأول صفحة
    const safeValue = toPositiveInteger(perPageInput, perPage);
    setPerPageInput(safeValue);
    setPerPage(safeValue);
    setLocalCurrentPage(1);
  };

  // Fetch all salaries once (no server-side search)
  const fetchAllSalaries = useCallback(async () => {
    try {
      const params = {
        language: i18n.language,
        page: 1,
        per_page: 1000,
      };
      const result = await dispatch(getSalaries(params)).unwrap();
      const data = result?.data || [];
      setRawSalaries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch salaries:', err);
      setRawSalaries([]);
    }
  }, [dispatch, i18n.language]);

  useEffect(() => {
    fetchAllSalaries();
  }, [fetchAllSalaries]);

  // Reset page on search or sort change
  useEffect(() => {
    setLocalCurrentPage(1);
  }, [searchTerm, sortField, sortOrder]);

  // Helper function to get employee name based on language
  const getEmployeeName = (employee) => {
    if (!employee) return '—';
    if (isRTL) {
      return employee.name_ar || employee.name || '—';
    } else {
      return employee.name_en || employee.name || '—';
    }
  };

  // Client-side filtering + sorting
  const filteredData = React.useMemo(() => {
    let data = rawSalaries;
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      data = data.filter((salary) => {
        const emp = salary.employee || {};
        return (
          (emp.name_ar || '').toLowerCase().includes(q) ||
          (emp.name_en || '').toLowerCase().includes(q) ||
          (emp.name || '').toLowerCase().includes(q) ||
          (emp.employee_code || '').toLowerCase().includes(q) ||
          (emp.phone || '').toLowerCase().includes(q) ||
          String(salary.month || '').toLowerCase().includes(q) ||
          String(salary.year || '').toLowerCase().includes(q)
        );
      });
    }
    return [...data].sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'name') {
        aVal = (getEmployeeName(a.employee) || '').toLowerCase();
        bVal = (getEmployeeName(b.employee) || '').toLowerCase();
      } else if (sortField === 'base_salary') {
        aVal = parseFloat(a.base_salary || 0);
        bVal = parseFloat(b.base_salary || 0);
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'effective_date') {
        aVal = new Date(a.effective_date || 0).getTime();
        bVal = new Date(b.effective_date || 0).getTime();
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'month') {
        aVal = parseInt(a.month || 0);
        bVal = parseInt(b.month || 0);
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'employee_code') {
        aVal = (a.employee?.employee_code || '').toLowerCase();
        bVal = (b.employee?.employee_code || '').toLowerCase();
      } else {
        aVal = (getEmployeeName(a.employee) || '').toLowerCase();
        bVal = (getEmployeeName(b.employee) || '').toLowerCase();
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rawSalaries, searchTerm, sortField, sortOrder]);

  // Local pagination
  const paginatedSalaries = React.useMemo(() => {
    const startIndex = (localCurrentPage - 1) * perPage;
    const endIndex = startIndex + perPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, localCurrentPage, perPage]);

  const totalPages = Math.ceil(filteredData.length / perPage);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: isRTL });
        lastSuccessRef.current = { message: success, time: now };
      }
      dispatch(clearSuccess());
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, isRTL]);

  const handlePageChange = (page) => {
    setLocalCurrentPage(page);
  };



  const formatCurrency = (amount) => {
    const formatted = parseFloat(amount).toLocaleString('en-US');
    return convertArabicNumbersToEnglish(formatted);
  };

  const formatDate = (dateString) => {
    const formatted = new Date(dateString).toLocaleDateString('en-US');
    return convertArabicNumbersToEnglish(formatted);
  };

  const preloadImage = src => new Promise(resolve => {
    if (!src) return resolve();
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });

  const calculateDynamicChunks = (items) => {
    const tableContainer = document.getElementById('salaries-measurement-container');
    if (!tableContainer) {
      const fallbackChunks = [];
      for (let i = 0; i < items.length; i += 10) {
        fallbackChunks.push(items.slice(i, i + 10));
      }
      return fallbackChunks;
    }

    const thead = tableContainer.querySelector('thead');
    const theadHeight = thead ? thead.offsetHeight : 35;
    const rows = tableContainer.querySelectorAll('tbody tr');
    
    const MAX_TABLE_HEIGHT = 740;
    const chunks = [];
    let currentChunk = [];
    let currentHeight = theadHeight;

    items.forEach((item, index) => {
      const rowElement = rows[index];
      const rowHeight = rowElement ? rowElement.offsetHeight : 45;

      if (currentHeight + rowHeight > MAX_TABLE_HEIGHT && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentHeight = theadHeight;
      }
      currentChunk.push(item);
      currentHeight += rowHeight;
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    return chunks;
  };

  // Handle print single salary record
  const handlePrintSalary = async (salary) => {
    try {
      setSalaryForPrint(salary);
      setPdfType('single');
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise((resolve) => setTimeout(resolve, 400));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`salary-pdf-${salary.id}`);
      if (!element) {
        toast.error(t('salaries.print.error', { defaultValue: 'خطأ في تحميل قالب الطباعة' }), { rtl: isRTL });
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        letterRendering: false,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById(`salary-pdf-${salary.id}`);
          if (clonedElement) {
            clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = 'bold';
            clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
            clonedElement.style.setProperty('-webkit-font-smoothing', 'antialiased');
            clonedElement.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
            clonedElement.style.textRendering = 'optimizeLegibility';

            const allElements = clonedElement.querySelectorAll('*');
            allElements.forEach(el => {
              if (el.style) {
                el.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = 'bold';
                el.style.setProperty('-webkit-font-smoothing', 'antialiased');
                el.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
                el.style.textRendering = 'optimizeLegibility';
              }
            });
          }
        }
      });

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        toast.error(t('salaries.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL('image/png', 1.0);
      let imgWidth = pdfWidth;
      let imgHeight = (canvas.height * pdfWidth) / canvas.width;

      const tolerance = 10;
      if (imgHeight > pdfHeight && imgHeight <= pdfHeight + tolerance) {
        const scaleFactor = pdfHeight / imgHeight;
        imgHeight = pdfHeight;
        imgWidth = pdfWidth * scaleFactor;
        const xOffset = (pdfWidth - imgWidth) / 2;
        pdf.addImage(imgData, 'PNG', xOffset, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else if (imgHeight <= pdfHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else {
        let position = 0;
        while (position < imgHeight) {
          if (position > 0) pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, -position, imgWidth, imgHeight, undefined, 'FAST');
          position += pdfHeight;
        }
      }

      const identifier = salary.employee?.employee_code || getEmployeeName(salary.employee) || salary.id;
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ACE_Salary_${identifier}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Salary PDF error:', err);
      toast.error(t('salaries.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setSalaryForPrint(null);
      setPdfType(null);
    }
  };

  // Handle preview all salary records
  const handlePreviewAll = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(t('salaries.print.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    const chunks = calculateDynamicChunks(filteredData);
    setPdfChunks(chunks);
    setPdfType('all');
    setIsPreviewOpen(true);
  };

  // Handle preview single salary record
  const handlePreviewSingle = (salary) => {
    setSingleSalaryForPreview(salary);
    setIsSinglePreviewOpen(true);
  };

  // Handle export all salaries to Excel
  const handleExportAllToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(t('salaries.export.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    try {
      const data = filteredData.map((salary, index) => ({
        [t('common.serialNumber', { defaultValue: '#' })]: sortOrder === 'asc' ? index + 1 : filteredData.length - index,
        [t('salaries.tableHeaders.employee', { defaultValue: 'الموظف' })]: getEmployeeName(salary.employee) || salary.employee_name || '—',
        [t('salaries.tableHeaders.code', { defaultValue: 'كود الموظف' })]: salary.employee?.employee_code || '—',
        [t('salaries.tableHeaders.month', { defaultValue: 'الشهر' })]: salary.month || '—',
        [t('salaries.tableHeaders.year', { defaultValue: 'السنة' })]: salary.year || '—',
        [t('salaries.tableHeaders.baseSalary', { defaultValue: 'الراتب الأساسي' })]: salary.base_salary || salary.salary || 0,
        [t('salaries.tableHeaders.allowances', { defaultValue: 'البدلات' })]: salary.allowances ? [
          salary.allowances.housing && `${t('salaries.allowances.housing')}: ${formatCurrency(salary.allowances.housing)}`,
          salary.allowances.transportation && `${t('salaries.allowances.transportation')}: ${formatCurrency(salary.allowances.transportation)}`
        ].filter(Boolean).join(' | ') || '—' : '—',
        [t('salaries.tableHeaders.deductions', { defaultValue: 'الخصومات' })]: salary.deductions?.social_insurance ? formatCurrency(salary.deductions.social_insurance) : '—',
        [t('salaries.tableHeaders.advances', { defaultValue: 'السلف' })]: salary.advances && salary.advances.length > 0 ? salary.advances.map(adv => formatCurrency(adv.remaining_balance || adv.amount)).join(', ') : '—',
        [t('salaries.tableHeaders.penalties', { defaultValue: 'الجزاءات' })]: salary.penalties && salary.penalties.length > 0 ? salary.penalties.map(pen => formatCurrency(pen.amount)).join(', ') : '—',
        [t('salaries.tableHeaders.effectiveDate', { defaultValue: 'تاريخ السريان' })]: salary.effective_date ? formatDate(salary.effective_date) : '—',
        [t('salaries.tableHeaders.total', { defaultValue: 'الإجمالي' })]: salary.total || 0
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('salaries.excel.sheetName', { defaultValue: 'Salaries' }));
      const fileName = `${t('salaries.excel.fileName', { defaultValue: 'Salaries' })}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(t('salaries.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('salaries.export.error', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  // Handle print all salary records
  const handlePrintAll = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(t('salaries.print.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      const chunks = calculateDynamicChunks(filteredData);
      setPdfChunks(chunks);
      setPdfType('all');

      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise((resolve) => setTimeout(resolve, 800));
      setIsGeneratingPdf(true);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => setTimeout(resolve, 100));

        const element = document.getElementById(`salaries-pdf-all-${chunkIndex}`);
        if (!element) {
          if (chunkIndex === 0) {
            toast.error(t('salaries.print.error', { defaultValue: 'قالب الطباعة غير متوفر' }), { rtl: isRTL });
            setIsGeneratingPdf(false);
            setPdfType(null);
            setPdfChunks([]);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.getElementById(`salaries-pdf-all-${chunkIndex}`);
            if (clonedElement) {
              clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = 'bold';
              clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
            }
          }
        });

        const imgData = canvas.toDataURL('image/png', 1.0);

        if (chunkIndex > 0) {
          pdf.addPage('a4', 'p');
        }

        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');
      }

      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ACE_Salaries_All_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t('salaries.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (error) {
      console.error('Salaries PDF error:', error);
      toast.error(t('salaries.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء ملف الرواتب' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir
    }}>
      {/* العنوان والبحث */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginBottom: '20px',
          color: 'white',
          textAlign
        }}>
          {t('salaries.title')}
        </h1>

        {/* شريط البحث */}
        <div style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '300px' }}>
            <FaSearch style={{
              position: 'absolute',
              [isRTL ? 'right' : 'left']: '15px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              fontSize: '16px'
            }} />
            <input
              type="text"
              placeholder={t('salaries.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: isRTL ? '12px 45px 12px 15px' : '12px 15px 12px 45px',
                backgroundColor: '#1b3350',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                textAlign,
                direction: dir
              }}
            />
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160, color: 'white', fontSize: 13, direction: isRTL ? 'rtl' : 'ltr' }}>
            <span>{t('common.sortBy', { defaultValue: 'ترتيب حسب' })}</span>
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              style={{ padding: '10px 12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr' }}
            >
              <option value="name">{t('salaries.tableHeaders.employee', { defaultValue: 'اسم الموظف' })}</option>
              <option value="employee_code">{t('salaries.tableHeaders.code', { defaultValue: 'كود الموظف' })}</option>
              <option value="base_salary">{t('salaries.tableHeaders.baseSalary', { defaultValue: 'الراتب الأساسي' })}</option>
              <option value="effective_date">{t('salaries.tableHeaders.effectiveDate', { defaultValue: 'تاريخ السريان' })}</option>
              <option value="month">{t('salaries.tableHeaders.month', { defaultValue: 'الشهر' })}</option>
            </select>
          </label>
          <SortOrderSelect value={sortOrder} onChange={(val) => setSortOrder(val)} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}>
              {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => {
                  const newValue = Math.max(1, toPositiveInteger(perPageInput, perPage) - 1);
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #333',
                  padding: '6px 10px',
                  borderRadius: '6px 0 0 6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '32px'
                }}
              >
                −
              </button>
              <input
                type="number"
                min="1"
                step="1"
                value={perPageInput}
                onChange={(e) => {
                  setPerPageInput(toPositiveInteger(e.target.value, perPage));
                }}
                style={{
                  width: '70px',
                  padding: '6px 10px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderRadius: 0,
                  color: 'white',
                  fontSize: '14px',
                  textAlign: 'center'
                }}
              />
              <button
                onClick={() => {
                  const newValue = toPositiveInteger(perPageInput, perPage) + 1;
                  setPerPageInput(newValue);
                }}
                style={{
                  backgroundColor: '#1b3350',
                  color: 'white',
                  border: '1px solid #333',
                  padding: '6px 10px',
                  borderRadius: '0 6px 6px 0',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '32px'
                }}
              >
                +
              </button>
            </div>
            <button
              onClick={handleApplyPerPage}
              style={{
                backgroundColor: '#AC2000',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
            >
              {t('common.apply', { defaultValue: 'تطبيق' })}
            </button>
          </div>

          <button
            onClick={() => navigate('/salaries/add')}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaPlus />
            {t('salaries.addButton')}
          </button>
          <button
            onClick={handlePreviewAll}
            style={{
              backgroundColor: '#444',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaEye /> {t('salaries.preview.all')}
          </button>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse',
              opacity: isGeneratingPdf ? 0.6 : 1
            }}
          >
            <FaPrint />
            {isGeneratingPdf ? t('salaries.print.generating', { defaultValue: 'جاري الطباعة...' }) : t('salaries.print.all', { defaultValue: 'طباعة PDF للكل' })}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaFileExcel /> {t('salaries.export.all', { defaultValue: 'تصدير Excel للكل' })}
          </button>
        </div>
      </div>

      {/***جدول الرواتب **/}
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #333'
      }}>
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #333',
          backgroundColor: '#1a1f2e'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 'bold',
            color: 'white',
            textAlign
          }}>
            {t('salaries.tableTitle')}
          </h3>
        </div>

        {isLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px',
            color: 'white'
          }}>
            <div>{t('salaries.loading')}</div>
          </div>
        ) : filteredData && filteredData.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '1000px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#1a1f2e' }}>
                  <th style={{
                    padding: '15px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid #333'
                  }}>
                    {t('salaries.table.number')}
                  </th>
                  <th style={{
                    padding: '15px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid #333'
                  }}>
                    {t('salaries.table.employee')}
                  </th>
                  <th style={{
                    padding: '15px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid #333'
                  }}>
                    {t('salaries.table.baseSalary')}
                  </th>
                  <th style={{
                    padding: '15px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid #333'
                  }}>
                    {t('salaries.table.allowances')}
                  </th>
                  <th style={{
                    padding: '15px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid #333'
                  }}>
                    {t('salaries.table.deductions')}
                  </th>
                  <th style={{
                    padding: '15px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid #333'
                  }}>
                    {t('salaries.table.advances', { defaultValue: 'السلف' })}
                  </th>
                  <th style={{
                    padding: '15px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid #333'
                  }}>
                    {t('salaries.table.penalties', { defaultValue: 'الجزاءات' })}
                  </th>
                  <th style={{
                    padding: '15px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid #333'
                  }}>
                    {t('salaries.table.effectiveDate')}
                  </th>
                  <th style={{
                    padding: '15px',
                    textAlign: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid #333'
                  }}>
                    {t('salaries.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
           
                {paginatedSalaries.map((salary, index) => {

                  const serialNumber = sortOrder === 'asc'
                    ? (localCurrentPage - 1) * perPage + index + 1
                    : filteredData.length - ((localCurrentPage - 1) * perPage + index);
                  return (
                    <tr key={salary.id} style={{
                      borderBottom: '2px solid #444',
                      borderTop: index === 0 ? '2px solid #444' : 'none'
                    }}>
                      <td style={{
                        padding: '15px',
                        textAlign: 'center',
                        color: 'white'
                      }}>
                        {serialNumber}
                      </td>
                      <td style={{
                        padding: '15px',
                        textAlign: 'center',
                        color: 'white'
                      }}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{getEmployeeName(salary.employee)}</div>
                          <div style={{ fontSize: '12px', color: '#ccc' }}>{salary.employee?.employee_code}</div>
                          <div style={{ fontSize: '12px', color: '#ccc' }}>{salary.employee?.phone}</div>
                        </div>
                      </td>
                      <td style={{
                        padding: '15px',
                        textAlign: 'center',
                        color: 'white'
                      }}>
                        {formatCurrency(salary.base_salary)} {t('salaries.currency')}
                      </td>
                      <td style={{
                        padding: '15px',
                        textAlign: 'center',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          {salary.allowances?.housing && (
                            <div style={{ textAlign: 'center' }}>{t('salaries.allowances.housing')}: {formatCurrency(salary.allowances.housing)} {t('salaries.currency')}</div>
                          )}
                          {salary.allowances?.transportation && (
                            <div style={{ textAlign: 'center' }}>{t('salaries.allowances.transportation')}: {formatCurrency(salary.allowances.transportation)} {t('salaries.currency')}</div>
                          )}
                          {!salary.allowances?.housing && !salary.allowances?.transportation && (
                            <div style={{ textAlign: 'center' }}>—</div>
                          )}
                        </div>
                      </td>
                      <td style={{
                        padding: '15px',
                        textAlign: 'center',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', textAlign: 'center' }}>
                          {salary.deductions?.social_insurance ? (
                            <div style={{ textAlign: 'center' }}>{t('salaries.deductions.socialInsurance')}: {formatCurrency(salary.deductions.social_insurance)} {t('salaries.currency')}</div>
                          ) : (
                            <div style={{ textAlign: 'center' }}>—</div>
                          )}
                        </div>
                      </td>
                      <td style={{
                        padding: '15px',
                        textAlign: 'center',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          {salary.advances && salary.advances.length > 0 ? (
                            salary.advances.map((advance, advIdx) => (
                              <div key={advIdx} style={{ textAlign: 'center' }}>
                                {formatCurrency(advance.remaining_balance || advance.amount)} {t('salaries.currency')}
                                {advance.description && <span style={{ fontSize: '10px', color: '#aaa' }}> ({advance.description})</span>}
                              </div>
                            ))
                          ) : (
                            <div style={{ textAlign: 'center' }}>—</div>
                          )}
                        </div>
                      </td>
                      <td style={{
                        padding: '15px',
                        textAlign: 'center',
                        color: 'white'
                      }}>
                        <div style={{ fontSize: '12px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          {salary.penalties && salary.penalties.length > 0 ? (
                            salary.penalties.map((penalty, penIdx) => (
                              <div key={penIdx} style={{ textAlign: 'center' }}>
                                {formatCurrency(penalty.amount)} {t('salaries.currency')}
                                {penalty.reason && <span style={{ fontSize: '10px', color: '#aaa' }}> ({penalty.reason})</span>}
                              </div>
                            ))
                          ) : (
                            <div style={{ textAlign: 'center' }}>—</div>
                          )}
                        </div>
                      </td>
                      <td style={{
                        padding: '15px',
                        textAlign: 'center',
                        color: 'white'
                      }}>
                        {formatDate(salary.effective_date)}
                      </td>
                      <td style={{ padding: '15px' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                          <button
                            onClick={() => handlePreviewSingle(salary)}
                            style={{
                              backgroundColor: '#666',
                              color: 'white',
                              border: 'none',
                              padding: '8px 10px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              flexShrink: 0
                            }}
                            title={t('salaries.preview.all')}
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => navigate(`/salaries/edit/${salary.employee?.id}`)}
                            style={{
                              backgroundColor: '#AC2000',
                              color: 'white',
                              border: 'none',
                              padding: '8px 10px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              flexShrink: 0
                            }}
                            title={t('salaries.actions.edit', { defaultValue: 'تعديل' })}
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handlePrintSalary(salary)}
                            disabled={isGeneratingPdf}
                            style={{
                              backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                              color: 'white',
                              border: 'none',
                              padding: '8px 10px',
                              borderRadius: '6px',
                              cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              opacity: isGeneratingPdf ? 0.6 : 1,
                              flexShrink: 0
                            }}
                            title={t('salaries.print.single', { defaultValue: 'طباعة' })}
                          >
                            <FaPrint />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#666'
          }}>
            {t('salaries.noRecords')}
          </div>
        )}


        {totalPages > 0 && (() => {
          const current_page = localCurrentPage;
          const last_page = totalPages;
          const pages = [];
          for (let i = 1; i <= last_page; i++) {
            if (i === 1 || i === last_page || (i >= current_page - 2 && i <= current_page + 2)) {
              pages.push(i);
            } else if (i === current_page - 3 || i === current_page + 3) {
              pages.push('...');
            }
          }
          return (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', paddingBottom: '20px', flexWrap: 'wrap' }}>
              <button
                onClick={() => handlePageChange(Math.max(1, current_page - 1))}
                disabled={current_page === 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: current_page === 1 ? '#333' : '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: current_page === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                {t('salaries.pagination.previous', { defaultValue: 'السابق' })}
              </button>
              {pages.map((p, idx) =>
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{ padding: '8px', color: 'white' }}>...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: p === current_page ? '#AC2000' : '#1a1f2e',
                      color: 'white',
                      border: '1px solid #333',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => handlePageChange(Math.min(last_page, current_page + 1))}
                disabled={current_page === last_page}
                style={{
                  padding: '8px 12px',
                  backgroundColor: current_page === last_page ? '#333' : '#AC2000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: current_page === last_page ? 'not-allowed' : 'pointer'
                }}
              >
                {t('salaries.pagination.next', { defaultValue: 'التالي' })}
              </button>
            </div>
          );
        })()}
      </div>

      {/* Print Templates */}
      {/* Single Salary Record Print Template */}
      {salaryForPrint && pdfType === 'single' && (
        <div
          id={`salary-pdf-${salaryForPrint.id}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '794px',
            minHeight: '1123px',
            height: 'auto',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            padding: '40px',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}
        >
          <div style={{
            border: '5px solid #000',
            padding: '3px',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            minHeight: '1043px',
            height: 'auto',
            overflow: 'visible'
          }}>
            <div style={{
              border: '2px solid #000',
              minHeight: '1033px',
              height: '1033px',
              padding: '18px 18px 10px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              textRendering: 'optimizeLegibility',
              direction: isRTL ? 'rtl' : 'ltr',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              overflow: 'visible'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: '95%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.08,
                zIndex: 0,
                pointerEvents: 'none'
              }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    marginBottom: '8px',
                    borderBottom: '4px double #000',
                    paddingBottom: '8px',
                    flexShrink: 0
                  }}
                  crossOrigin="anonymous"
                />

                <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
                  <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير الرواتب' : 'Salaries Report'}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}># {salaryForPrint.employee?.employee_code || salaryForPrint.id}</div>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                  <div style={{
                    marginTop: '12px',
                    border: '2px solid #000',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)'
                  }}>
                    {[
                      { label: t('salaries.print.labels.employeeName', { defaultValue: 'اسم الموظف' }), value: salaryForPrint.employee?.name || '—' },
                      { label: t('salaries.print.labels.employeeCode', { defaultValue: 'كود الموظف' }), value: salaryForPrint.employee?.employee_code || '—' },
                      { label: t('salaries.print.labels.baseSalary', { defaultValue: 'الراتب الأساسي' }), value: `${formatCurrency(salaryForPrint.base_salary)} ${t('salaries.currency')}`, accent: true },
                      {
                        label: t('salaries.print.labels.allowances', { defaultValue: 'البدلات' }), value: (() => {
                          if (!salaryForPrint.allowances) return '—';
                          const allowancesList = [
                            salaryForPrint.allowances.housing && `${t('salaries.allowances.housing')}: ${formatCurrency(salaryForPrint.allowances.housing)} ${t('salaries.currency')}`,
                            salaryForPrint.allowances.transportation && `${t('salaries.allowances.transportation')}: ${formatCurrency(salaryForPrint.allowances.transportation)} ${t('salaries.currency')}`
                          ].filter(Boolean);
                          return allowancesList.length > 0 ? allowancesList.join(' | ') : '—';
                        })()
                      },
                      { label: t('salaries.print.labels.deductions', { defaultValue: 'الخصومات' }), value: salaryForPrint.deductions?.social_insurance ? `${t('salaries.deductions.socialInsurance')}: ${formatCurrency(salaryForPrint.deductions.social_insurance)} ${t('salaries.currency')}` : '—' },
                      { 
                        label: t('salaries.print.labels.advances', { defaultValue: 'السلف' }), 
                        value: salaryForPrint.advances && salaryForPrint.advances.length > 0 ? salaryForPrint.advances.map(adv => `${formatCurrency(adv.remaining_balance || adv.amount)} ${t('salaries.currency')}`).join(' | ') : '—' 
                      },
                      { 
                        label: t('salaries.print.labels.penalties', { defaultValue: 'الجزاءات' }), 
                        value: salaryForPrint.penalties && salaryForPrint.penalties.length > 0 ? salaryForPrint.penalties.map(pen => `${formatCurrency(pen.amount)} ${t('salaries.currency')}`).join(' | ') : '—' 
                      },
                      { label: t('salaries.print.labels.effectiveDate', { defaultValue: 'تاريخ السريان' }), value: formatDate(salaryForPrint.effective_date) || '—', accent: true }
                    ].filter(row => row.value && row.value !== '—').map((row) => (
                      <div key={row.label} style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                        <div style={{
                          width: '30%',
                          borderLeft: '1px solid #000',
                          backgroundColor: 'rgba(227, 242, 253, 0.4)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          fontWeight: '700',
                          fontSize: '14px',
                          direction: isRTL ? 'rtl' : 'ltr',
                          color: '#1976d2',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                          fontWeight: 'bold',
                          padding: '0 10px'
                        }}>
                          {row.label}
                        </div>
                        <div style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          fontSize: '15px',
                          fontWeight: row.accent ? '700' : '500',
                          color: row.accent ? '#0057c2' : '#000',
                          direction: isRTL ? 'rtl' : 'ltr',
                          padding: '0 10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                          fontWeight: 'bold'
                        }}>
                          {row.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '18px',
                  right: '18px',
                  flexShrink: 0
                }}>
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                        borderTop: "4px double #000",
                        paddingTop: "8px",
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Salary Records Print Template */}
      {pdfType === 'all' && pdfChunks && pdfChunks.length > 0 && (
        <>
          {pdfChunks.map((chunk, chunkIndex) => (
            <div
              key={chunkIndex}
              id={`salaries-pdf-all-${chunkIndex}`}
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-9999px',
                top: 0,
                width: '794px',
                height: '1123px',
                backgroundColor: '#ffffff',
                color: '#000000',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                padding: '40px',
                boxSizing: 'border-box',
                overflow: 'hidden'
              }}
            >
              <div style={{
                border: '5px solid #000',
                padding: '3px',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                height: '1043px',
                overflow: 'hidden'
              }}>
                <div style={{
                  border: '2px solid #000',
                  height: '1033px',
                  padding: '18px 18px 10px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: 'bold',
                  WebkitFontSmoothing: 'antialiased',
                  MozOsxFontSmoothing: 'grayscale',
                  textRendering: 'optimizeLegibility',
                  direction: isRTL ? 'rtl' : 'ltr',
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box',
                  width: '100%',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${headerLogo})`,
                    backgroundSize: '95%',
                    backgroundPosition: 'center center',
                    backgroundRepeat: 'no-repeat',
                    opacity: 0.08,
                    zIndex: 0,
                    pointerEvents: 'none'
                  }} />
                  <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        marginBottom: '8px',
                        borderBottom: '4px double #000',
                        paddingBottom: '8px',
                        flexShrink: 0
                      }}
                      crossOrigin="anonymous"
                    />

                    <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
                      <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير الرواتب' : 'Salaries Report'}</div>
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>{isRTL ? 'تاريخ التقرير' : 'Report Date'}: {new Date().toLocaleDateString('en-US')} — {isRTL ? 'صفحة' : 'Page'} {chunkIndex + 1} / {pdfChunks.length}</div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingBottom: '16px' }}>
                      <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                          <thead>
                            <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>#</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('salaries.tableHeaders.employee', { defaultValue: 'اسم الموظف' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('salaries.tableHeaders.code', { defaultValue: 'كود الموظف' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('salaries.tableHeaders.baseSalary', { defaultValue: 'الراتب الأساسي' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('salaries.tableHeaders.allowances', { defaultValue: 'البدلات' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('salaries.tableHeaders.deductions', { defaultValue: 'الخصومات' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('salaries.tableHeaders.advances', { defaultValue: 'السلف' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('salaries.tableHeaders.penalties', { defaultValue: 'الجزاءات' })}</th>
                              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('salaries.tableHeaders.effectiveDate', { defaultValue: 'تاريخ السريان' })}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {chunk.map((salary, idx) => {
                              const total = filteredData.length;
                              const globalIdx = filteredData.findIndex(s => String(s?.id) === String(salary?.id));
                              const safeGlobalIdx = globalIdx >= 0 ? globalIdx : idx;
                              const serialNumber = sortOrder === 'asc' ? safeGlobalIdx + 1 : total - safeGlobalIdx;
                              return (
                                <tr key={salary.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{serialNumber}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', fontSize: '10px', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{getEmployeeName(salary.employee) || '—'}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{salary.employee?.employee_code || '—'}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatCurrency(salary.base_salary || salary.salary || 0)}</td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                                    {salary.allowances ? [
                                      salary.allowances.housing && `${t('salaries.allowances.housing')}: ${formatCurrency(salary.allowances.housing)}`,
                                      salary.allowances.transportation && `${t('salaries.allowances.transportation')}: ${formatCurrency(salary.allowances.transportation)}`
                                    ].filter(Boolean).join(' | ') || '—' : '—'}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                                    {salary.deductions?.social_insurance ? `${formatCurrency(salary.deductions.social_insurance)}` : '—'}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '9px', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                                    {salary.advances && salary.advances.length > 0 ? salary.advances.map(adv => formatCurrency(adv.remaining_balance || adv.amount)).join(', ') : '—'}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '9px', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                                    {salary.penalties && salary.penalties.length > 0 ? salary.penalties.map(pen => formatCurrency(pen.amount)).join(', ') : '—'}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatDate(salary.effective_date)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
                      <img
                        src={`${publicUrl}/FooterAce.png`}
                        alt="Footer"
                        style={{
                          width: '100%',
                          height: 'auto',
                            borderTop: "4px double #000",
                            paddingTop: "8px",
                          objectFit: 'contain',
                          display: 'block'
                        }}
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Preview Modal - All Salaries */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
        title={t('salaries.title', { defaultValue: 'الرواتب' })}
        onClose={() => {
          setIsPreviewOpen(false);
          setPdfChunks([]);
          setPdfType(null);
        }}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          {pdfChunks.map((chunk, chunkIdx) => (
            <div key={chunkIdx} style={{
              width: '794px',
              height: '1123px',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              overflow: 'hidden'
            }}>
              <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
                <div style={{
                  border: '2px solid #000', padding: '18px 18px 10px', position: 'relative',
                  fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: 'bold',
                  direction: isRTL ? 'rtl' : 'ltr', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1033px', overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundImage: `url(${headerLogo})`, backgroundSize: '95%',
                    backgroundPosition: 'center center', backgroundRepeat: 'no-repeat',
                    opacity: 0.08, zIndex: 0, pointerEvents: 'none'
                  }} />
                  <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <img src={`${publicUrl}/HeaderAce.png`} alt="Header"
                      style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', display: 'block', flexShrink: 0 }}
                      crossOrigin="anonymous"
                    />
                    <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '8px', direction: isRTL ? 'rtl' : 'ltr', flexShrink: 0 }}>
                      <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير الرواتب' : 'Salaries Report'}</div>
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>{isRTL ? 'تاريخ التقرير' : 'Report Date'}: {new Date().toLocaleDateString('en-US')} {pdfChunks.length > 1 ? `— ${isRTL ? 'صفحة' : 'Page'} ${chunkIdx + 1}/${pdfChunks.length}` : ''}</div>
                    </div>
                    <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif', flex: 1 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>#</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.employee', { defaultValue: 'اسم الموظف' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.code', { defaultValue: 'كود الموظف' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.baseSalary', { defaultValue: 'الراتب الأساسي' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.allowances', { defaultValue: 'البدلات' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.deductions', { defaultValue: 'الخصومات' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.advances', { defaultValue: 'السلف' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.penalties', { defaultValue: 'الجزاءات' })}</th>
                            <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.effectiveDate', { defaultValue: 'تاريخ السريان' })}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {chunk.map((salary, idx) => {
                            const gIdx = filteredData.findIndex(s => String(s?.id) === String(salary?.id));
                            const safeGIdx = gIdx >= 0 ? gIdx : idx;
                            const previewSerial = sortOrder === 'asc' ? safeGIdx + 1 : filteredData.length - safeGIdx;
                            return (
                            <tr key={salary.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>{previewSerial}</td>
                              <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', fontSize: '10px' }}>{getEmployeeName(salary.employee) || '—'}</td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>{salary.employee?.employee_code || '—'}</td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>{formatCurrency(salary.base_salary || salary.salary || 0)}</td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>
                                {salary.allowances ? [
                                  salary.allowances.housing && `${t('salaries.allowances.housing')}: ${formatCurrency(salary.allowances.housing)}`,
                                  salary.allowances.transportation && `${t('salaries.allowances.transportation')}: ${formatCurrency(salary.allowances.transportation)}`
                                ].filter(Boolean).join(' | ') || '—' : '—'}
                              </td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>
                                {salary.deductions?.social_insurance ? formatCurrency(salary.deductions.social_insurance) : '—'}
                              </td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '9px' }}>
                                {salary.advances && salary.advances.length > 0 ? salary.advances.map(adv => formatCurrency(adv.remaining_balance || adv.amount)).join(', ') : '—'}
                              </td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '9px' }}>
                                {salary.penalties && salary.penalties.length > 0 ? salary.penalties.map(pen => formatCurrency(pen.amount)).join(', ') : '—'}
                              </td>
                              <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>{formatDate(salary.effective_date)}</td>
                            </tr>
                          ); })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
                      <img src={`${publicUrl}/FooterAce.png`} alt="Footer"
                        style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block', borderTop: '4px double #000', paddingTop: '8px' }}
                        crossOrigin="anonymous"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PdfPreviewModal>

      {/* Preview Modal - Single Salary */}
      <PdfPreviewModal
        isOpen={isSinglePreviewOpen && !!singleSalaryForPreview}
        title={t('salaries.preview.single', { defaultValue: 'معاينة الراتب' })}
        onClose={() => {
          setIsSinglePreviewOpen(false);
          setSingleSalaryForPreview(null);
        }}
        onPrint={() => singleSalaryForPreview && handlePrintSalary(singleSalaryForPreview)}
        isGeneratingPdf={isGeneratingPdf}
      >
        {singleSalaryForPreview && (
          <div style={{
            width: '794px',
            height: 'auto',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            padding: '40px',
            boxSizing: 'border-box',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
          }}>
            <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', minHeight: '1043px', height: 'auto' }}>
              <div style={{
                border: '2px solid #000', minHeight: '1033px', height: '1033px', padding: '18px 18px 10px',
                position: 'relative', display: 'flex', flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: 'bold',
                direction: isRTL ? 'rtl' : 'ltr', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%',
              }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  backgroundImage: `url(${headerLogo})`, backgroundSize: '95%',
                  backgroundPosition: 'center center', backgroundRepeat: 'no-repeat',
                  opacity: 0.08, zIndex: 0, pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                  <img src={`${publicUrl}/HeaderAce.png`} alt="Header"
                    style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', flexShrink: 0 }}
                    crossOrigin="anonymous"
                  />
                  <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
                    <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير الرواتب' : 'Salaries Report'}</div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}># {singleSalaryForPreview.employee?.employee_code || singleSalaryForPreview.id}</div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{ marginTop: '12px', border: '2px solid #000', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                      {[
                        { label: t('salaries.print.labels.employeeName', { defaultValue: 'اسم الموظف' }), value: singleSalaryForPreview.employee?.name || '—' },
                        { label: t('salaries.print.labels.employeeCode', { defaultValue: 'كود الموظف' }), value: singleSalaryForPreview.employee?.employee_code || '—' },
                        { label: t('salaries.print.labels.baseSalary', { defaultValue: 'الراتب الأساسي' }), value: `${formatCurrency(singleSalaryForPreview.base_salary)} ${t('salaries.currency')}`, accent: true },
                        {
                          label: t('salaries.print.labels.allowances', { defaultValue: 'البدلات' }), value: (() => {
                            if (!singleSalaryForPreview.allowances) return '—';
                            const list = [
                              singleSalaryForPreview.allowances.housing && `${t('salaries.allowances.housing')}: ${formatCurrency(singleSalaryForPreview.allowances.housing)} ${t('salaries.currency')}`,
                              singleSalaryForPreview.allowances.transportation && `${t('salaries.allowances.transportation')}: ${formatCurrency(singleSalaryForPreview.allowances.transportation)} ${t('salaries.currency')}`
                            ].filter(Boolean);
                            return list.length > 0 ? list.join(' | ') : '—';
                          })()
                        },
                        { label: t('salaries.print.labels.deductions', { defaultValue: 'الخصومات' }), value: singleSalaryForPreview.deductions?.social_insurance ? `${t('salaries.deductions.socialInsurance')}: ${formatCurrency(singleSalaryForPreview.deductions.social_insurance)} ${t('salaries.currency')}` : '—' },
                        { label: t('salaries.print.labels.advances', { defaultValue: 'السلف' }), value: singleSalaryForPreview.advances && singleSalaryForPreview.advances.length > 0 ? singleSalaryForPreview.advances.map(adv => `${formatCurrency(adv.remaining_balance || adv.amount)} ${t('salaries.currency')}`).join(' | ') : '—' },
                        { label: t('salaries.print.labels.penalties', { defaultValue: 'الجزاءات' }), value: singleSalaryForPreview.penalties && singleSalaryForPreview.penalties.length > 0 ? singleSalaryForPreview.penalties.map(pen => `${formatCurrency(pen.amount)} ${t('salaries.currency')}`).join(' | ') : '—' },
                        { label: t('salaries.print.labels.effectiveDate', { defaultValue: 'تاريخ السريان' }), value: formatDate(singleSalaryForPreview.effective_date) || '—', accent: true }
                      ].filter(row => row.value && row.value !== '—').map((row) => (
                        <div key={row.label} style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                          <div style={{
                            width: '30%', borderLeft: '1px solid #000', backgroundColor: 'rgba(227, 242, 253, 0.4)',
                            display: 'flex', alignItems: 'center', fontWeight: '700', fontSize: '14px',
                            direction: isRTL ? 'rtl' : 'ltr', color: '#1976d2',
                            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', padding: '0 10px'
                          }}>
                            {row.label}
                          </div>
                          <div style={{
                            flex: 1, display: 'flex', alignItems: 'center', fontSize: '15px',
                            fontWeight: row.accent ? '700' : '500', color: row.accent ? '#0057c2' : '#000',
                            direction: isRTL ? 'rtl' : 'ltr', padding: '0 10px', backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                          }}>
                            {row.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
                    <img src={`${publicUrl}/FooterAce.png`} alt="Footer"
                      style={{ width: '100%', height: 'auto', objectFit: 'contain',
              borderTop: "4px double #000",
              paddingTop: "8px",
}}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </PdfPreviewModal>
      {/* Hidden Measurement Table */}
      <div
        id="salaries-measurement-container"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '658px',
          visibility: 'hidden',
          pointerEvents: 'none'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif' }}>
          <thead>
            <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>#</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.employee', { defaultValue: 'اسم الموظف' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.code', { defaultValue: 'كود الموظف' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.baseSalary', { defaultValue: 'الراتب الأساسي' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.allowances', { defaultValue: 'البدلات' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.deductions', { defaultValue: 'الخصومات' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.advances', { defaultValue: 'السلف' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.penalties', { defaultValue: 'الجزاءات' })}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700' }}>{t('salaries.tableHeaders.effectiveDate', { defaultValue: 'تاريخ السريان' })}</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((salary, idx) => (
              <tr key={salary.id}>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', fontSize: '10px' }}>{getEmployeeName(salary.employee) || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>{salary.employee?.employee_code || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>{formatCurrency(salary.base_salary || salary.salary || 0)}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>
                  {salary.allowances ? [
                    salary.allowances.housing && `${t('salaries.allowances.housing')}: ${formatCurrency(salary.allowances.housing)}`,
                    salary.allowances.transportation && `${t('salaries.allowances.transportation')}: ${formatCurrency(salary.allowances.transportation)}`
                  ].filter(Boolean).join(' | ') || '—' : '—'}
                </td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>
                  {salary.deductions?.social_insurance ? `${formatCurrency(salary.deductions.social_insurance)}` : '—'}
                </td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '9px' }}>
                  {salary.advances && salary.advances.length > 0 ? salary.advances.map(adv => formatCurrency(adv.remaining_balance || adv.amount)).join(', ') : '—'}
                </td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '9px' }}>
                  {salary.penalties && salary.penalties.length > 0 ? salary.penalties.map(pen => formatCurrency(pen.amount)).join(', ') : '—'}
                </td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontSize: '10px' }}>{formatDate(salary.effective_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Salaries;

